/** Module-level toast queue shared by every shell's `<ToastViewport />`. */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

export type ToastTone = 'ok' | 'error' | 'info';

export interface ToastItem {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: string;
}

let toasts: readonly ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = (): void => {
  listeners.forEach((listener) => {
    listener();
  });
};

export function dismissToast(id: number): void {
  toasts = toasts.filter((item) => item.id !== id);
  emit();
}

function push(tone: ToastTone, message: string): number {
  const id = nextId++;
  toasts = [...toasts.slice(-3), { id, tone, message }];
  emit();
  // Errors stay until dismissed so they are never missed.
  if (tone !== 'error') {
    setTimeout(() => {
      dismissToast(id);
    }, 4000);
  }
  return id;
}

export const toast = {
  success: (message: string): number => push('ok', message),
  error: (message: string): number => push('error', message),
  info: (message: string): number => push('info', message),
};

export function useToasts(): readonly ToastItem[] {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => toasts,
  );
}

/** Counts ids present now that were absent at the previous render of `ids`. */
export function useNewIds(ids: readonly string[]): ReadonlySet<string> {
  const seen = useRef<Set<string> | null>(null);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  const key = ids.join('|');
  useEffect(() => {
    const current = new Set(ids);
    if (seen.current !== null) {
      const previous = seen.current;
      setFresh(new Set(ids.filter((id) => !previous.has(id))));
    }
    seen.current = current;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by joined ids
  }, [key]);
  return fresh;
}
